
import React, { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface DeleteAccountDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const DeleteAccountDialog = ({ isOpen, onOpenChange }: DeleteAccountDialogProps) => {
  const [confirmText, setConfirmText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { deleteAccount, user } = useAuth();
  const { toast } = useToast();

  const handleDeleteAccount = async () => {
    if (confirmText !== 'delete my account') {
      toast({
        title: 'Confirmation text doesn\'t match',
        description: 'Please type "delete my account" to confirm deletion',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await deleteAccount();
      
      if (result.success) {
        toast({
          title: 'Account deleted',
          description: 'Your account has been successfully deleted.',
        });
        onOpenChange(false);
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Failed to delete account',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-[425px]">
      <DialogHeader>
        <DialogTitle className="flex items-center text-destructive">
          <AlertTriangle className="h-5 w-5 mr-2" />
          Delete Account
        </DialogTitle>
        <DialogDescription>
          This action cannot be undone. Your account and all associated data will be permanently deleted.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-3 rounded-md">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            All your products, groups, and usage records will be permanently deleted.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm-text">
            Type <span className="font-semibold">delete my account</span> to confirm:
          </Label>
          <Input
            id="confirm-text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full"
            autoComplete="off"
          />
        </div>
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={handleDeleteAccount}
          disabled={confirmText !== 'delete my account' || isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Deleting...
            </>
          ) : (
            'Delete Account'
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

export default DeleteAccountDialog;
